import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useScope } from "@/hooks/use-scope";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Globe, Loader2, Pencil, Plus, ShieldCheck, Trash2 } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import {
  deleteSsoDomainMapping,
  listSsoDomainMappings,
  ssoRoles,
  upsertSsoDomainMapping,
} from "@/lib/sso-domain-mappings.functions";

export const Route = createFileRoute("/_authenticated/app/settings/sso")({
  head: () => ({
    meta: [
      { title: "SSO domain mapping — CairnOS" },
      {
        name: "description",
        content:
          "Map email domains to identity providers and assign a default role for first sign-in.",
      },
    ],
  }),
  component: SsoSettings,
});

type Row = {
  id: string;
  tenant_id: string;
  email_domain: string;
  idp_entity_id: string;
  default_role: string;
  created_at: string;
};

function SsoSettings() {
  const { tenantId } = useScope();
  const qc = useQueryClient();
  const listFn = useServerFn(listSsoDomainMappings);
  const upsertFn = useServerFn(upsertSsoDomainMapping);
  const delFn = useServerFn(deleteSsoDomainMapping);

  const [editing, setEditing] = useState<Row | null>(null);
  const [open, setOpen] = useState(false);

  const q = useQuery({
    queryKey: ["sso-domain-mappings", tenantId],
    enabled: !!tenantId,
    queryFn: () => listFn({ data: { tenantId: tenantId! } }),
  });

  const upsertMut = useMutation({
    mutationFn: (p: {
      id: string | null;
      emailDomain: string;
      idpEntityId: string;
      defaultRole: string;
    }) =>
      upsertFn({
        data: {
          id: p.id,
          tenantId: tenantId!,
          emailDomain: p.emailDomain,
          idpEntityId: p.idpEntityId,
          defaultRole: p.defaultRole as any,
        },
      }),
    onSuccess: () => {
      toast.success(editing ? "Mapping updated" : "Mapping created");
      setOpen(false);
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["sso-domain-mappings", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => delFn({ data: { id, tenantId: tenantId! } }),
    onSuccess: () => {
      toast.success("Mapping removed");
      void qc.invalidateQueries({ queryKey: ["sso-domain-mappings", tenantId] });
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const rows = (q.data?.rows ?? []) as Row[];

  return (
    <div className="p-6 space-y-6 max-w-6xl">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <ShieldCheck className="h-6 w-6" /> SSO domain mapping
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Route users to the right identity provider based on their email domain, and assign the
            role they receive on first sign-in. Configure SAML / OIDC providers in your IdP and
            paste their <span className="font-mono">EntityID</span> below.
          </p>
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
        >
          <Plus className="h-4 w-4 mr-2" /> Add domain
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Mapped Domains</CardTitle>
          <CardDescription>
            When a user signs in with <span className="font-mono">name@domain</span>, the matching
            mapping resolves the IdP and default role. One mapping per email domain.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {q.isLoading ? (
            <div className="text-sm text-muted-foreground flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground py-10 text-center flex flex-col items-center gap-3">
              <Globe className="h-8 w-8 text-muted-foreground/50" />
              <div>No SSO domain mappings yet.</div>
              <div className="text-xs">
                Add your first domain to enable SAML / OIDC for that company.
              </div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email domain</TableHead>
                  <TableHead>IdP EntityID</TableHead>
                  <TableHead>Default role</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell className="font-mono text-xs">@{r.email_domain}</TableCell>
                    <TableCell className="font-mono text-[11px] max-w-[420px] truncate" title={r.idp_entity_id}>
                      {r.idp_entity_id}
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px] font-mono">
                        {r.default_role}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditing(r);
                            setOpen(true);
                          }}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm(`Remove mapping for @${r.email_domain}?`)) {
                              delMut.mutate(r.id);
                            }
                          }}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <EditDialog
        open={open}
        editing={editing}
        submitting={upsertMut.isPending}
        onClose={() => {
          setOpen(false);
          setEditing(null);
        }}
        onSubmit={(p) => upsertMut.mutate(p)}
      />
    </div>
  );
}

function EditDialog({
  open, editing, onClose, onSubmit, submitting,
}: {
  open: boolean;
  editing: Row | null;
  onClose: () => void;
  submitting: boolean;
  onSubmit: (p: {
    id: string | null;
    emailDomain: string;
    idpEntityId: string;
    defaultRole: string;
  }) => void;
}) {
  const [emailDomain, setEmailDomain] = useState(editing?.email_domain ?? "");
  const [idpEntityId, setIdpEntityId] = useState(editing?.idp_entity_id ?? "");
  const [defaultRole, setDefaultRole] = useState(editing?.default_role ?? "operator");

  // Reset when dialog re-opens
  const key = editing?.id ?? "new";

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-w-lg" key={key}>
        <DialogHeader>
          <DialogTitle>{editing ? "Edit domain mapping" : "Add domain mapping"}</DialogTitle>
          <DialogDescription>
            Email domains must be globally unique across CairnOS.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="sso-domain">Email domain</Label>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">@</span>
              <Input
                id="sso-domain"
                defaultValue={emailDomain}
                onChange={(e) => setEmailDomain(e.target.value)}
                placeholder="acme.com"
                autoComplete="off"
              />
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">
              Users signing in with <span className="font-mono">…@acme.com</span> will be routed to
              the IdP below.
            </p>
          </div>
          <div>
            <Label htmlFor="sso-entity">IdP EntityID / Issuer URL</Label>
            <Input
              id="sso-entity"
              defaultValue={idpEntityId}
              onChange={(e) => setIdpEntityId(e.target.value)}
              placeholder="https://sts.windows.net/{tenant-guid}/"
              autoComplete="off"
              className="font-mono text-xs"
            />
          </div>
          <div>
            <Label htmlFor="sso-role">Default role on first sign-in</Label>
            <Select value={defaultRole} onValueChange={setDefaultRole}>
              <SelectTrigger id="sso-role">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ssoRoles.map((r) => (
                  <SelectItem key={r} value={r} className="font-mono text-xs">
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-muted-foreground mt-1">
              Existing users keep their current roles — this only applies to new accounts created
              via SSO.
            </p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={submitting}
            onClick={() =>
              onSubmit({
                id: editing?.id ?? null,
                emailDomain,
                idpEntityId,
                defaultRole,
              })
            }
          >
            {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {editing ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}